import { useState, type FormEvent } from "react";
import Field from "../../components/Field";
import TextArea from "../../components/TextArea";
import PhotoUpload from "../../components/PhotoUpload";
import FormMessage from "../../components/FormMessage";
import { ApiError, apiFetch } from "../../lib/api";
import { useAuth, type PublicUser } from "../../lib/auth";

export default function Profile() {
  const { user, refresh } = useAuth();
  if (!user) return null;
  return (
    <div style={{ maxWidth: 640 }}>
      <header style={{ marginBottom: "2rem" }}>
        <div className="eyebrow">Your profile</div>
        <h1
          className="display display--lg"
          style={{ marginTop: "0.5rem", marginBottom: 0 }}
        >
          your details.
        </h1>
      </header>

      <DetailsCard user={user} onSaved={refresh} />
      <MembershipCard user={user} />
    </div>
  );
}

// ---------- Details card (editable) ----------

function DetailsCard({
  user,
  onSaved,
}: {
  user: PublicUser;
  onSaved: () => Promise<void>;
}) {
  const [fullName, setFullName] = useState(user.fullName);
  const [preferredName, setPreferredName] = useState(user.preferredName ?? "");
  const [bio, setBio] = useState(user.bio ?? "");
  const [photoUrl, setPhotoUrl] = useState<string | null>(user.photoUrl);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSaving(true);
    try {
      await apiFetch("/api/me", {
        method: "PATCH",
        body: {
          fullName: fullName.trim(),
          preferredName: preferredName.trim() || null,
          bio: bio.trim() || null,
          photoUrl,
        },
      });
      await onSaved();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      style={{
        background: "#fff",
        border: "1px solid var(--c-rule)",
        borderRadius: "var(--r-md)",
        padding: "clamp(1.25rem, 3vw, 1.75rem)",
        marginBottom: "1.5rem",
      }}
    >
      {error && <FormMessage tone="error">{error}</FormMessage>}
      {success && <FormMessage tone="success">Saved.</FormMessage>}
      <form onSubmit={onSubmit} noValidate>
        <PhotoUpload
          label="Profile photo"
          value={photoUrl}
          onChange={setPhotoUrl}
        />
        <Field
          label="Full name"
          required
          value={fullName}
          onChange={(e) => setFullName(e.currentTarget.value)}
          maxLength={120}
        />
        <Field
          label="What friends call you"
          hint='Optional — shown in the directory if you set it.'
          value={preferredName}
          onChange={(e) => setPreferredName(e.currentTarget.value)}
          maxLength={60}
        />
        <TextArea
          label="Bio"
          rows={3}
          hint="A sentence or two. Shown on your member card."
          value={bio}
          onChange={(e) => setBio(e.currentTarget.value)}
          maxLength={500}
        />
        <Field
          label="Email"
          value={user.email}
          disabled
          hint="Email changes aren't supported yet — get in touch if you need to update yours."
        />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={saving}
          >
            {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </form>
    </section>
  );
}

// ---------- Membership card (placeholder until Stripe) ----------

function MembershipCard({ user }: { user: PublicUser }) {
  const isAdmin = user.role === "ADMIN";

  return (
    <section
      style={{
        background: "#fff",
        border: "1px solid var(--c-rule)",
        borderRadius: "var(--r-md)",
        padding: "clamp(1.25rem, 3vw, 1.75rem)",
      }}
    >
      <h2
        className="eyebrow"
        style={{ color: "var(--c-palm)", marginTop: 0, marginBottom: "1rem" }}
      >
        Membership
      </h2>

      {isAdmin ? (
        <p style={{ margin: 0, lineHeight: 1.6 }}>
          You're an admin — admins don't pay membership dues. Thanks for
          running the place. 💌
        </p>
      ) : (
        <>
          <Row label="Status" value={STATUS_LABEL[user.status]} />
          <Row
            label="Next renewal"
            value="—"
            note="Billing will turn on once Stripe is wired up."
          />
          <Row
            label="Payment method"
            value="—"
            note="No card on file yet."
          />
          <div
            style={{
              marginTop: "1.25rem",
              display: "flex",
              gap: "0.75rem",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              disabled
              title="Available once Stripe is set up"
              className="btn btn--ghost"
              style={{
                padding: "0.55rem 1rem",
                fontSize: "0.78rem",
                opacity: 0.5,
                cursor: "not-allowed",
              }}
            >
              Cancel membership
            </button>
            <span
              style={{
                fontSize: "0.8rem",
                color: "var(--c-palm)",
                opacity: 0.65,
              }}
            >
              Cancellation will be self-service once billing is on.
            </span>
          </div>
        </>
      )}
    </section>
  );
}

const STATUS_LABEL: Record<PublicUser["status"], string> = {
  PENDING: "Pending review",
  REJECTED: "Rejected",
  APPROVED_UNPAID: "Approved — payment not yet set up",
  ACTIVE: "Active",
  LAPSED: "Lapsed",
};

function Row({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <div className="kv-row">
      <div className="eyebrow" style={{ color: "var(--c-palm)", opacity: 0.7 }}>
        {label}
      </div>
      <div>
        <div>{value}</div>
        {note && (
          <div
            style={{
              fontSize: "0.8rem",
              color: "var(--c-palm)",
              opacity: 0.65,
              marginTop: 2,
            }}
          >
            {note}
          </div>
        )}
      </div>
    </div>
  );
}
